import { ReactNode, memo, useMemo } from 'react';

// Peças visuais do Criar Conteúdo IA: ícone por tipo de atividade, markdown simples,
// formatação de créditos e duração.

export type ActivityKind =
  | 'brand' | 'knowledge' | 'products' | 'avatars' | 'playbook' | 'delegate' | 'plan' | 'budget'
  | 'image' | 'video' | 'voice' | 'avatar_video' | 'broll' | 'research_profile' | 'research_post'
  | 'decupagem' | 'render' | 'caption' | 'schedule' | 'file' | 'look' | 'web' | 'tool';

// Cor e desenho de cada tipo. Os tipos das próximas fases (vídeo, voz, pesquisa...) já estão
// aqui para a tela não mudar quando as ferramentas forem ligadas.
const KIND_STYLE: Record<ActivityKind, { bg: string; fg: string; path: ReactNode }> = {
  brand: { bg: 'bg-indigo-100', fg: 'text-indigo-600', path: <><path d="M4 7h16" /><path d="M6 7v12" /><path d="M18 7v12" /><path d="M9 3h6l1 4H8l1-4Z" /><path d="M8 13h8" /></> },
  knowledge: { bg: 'bg-sky-100', fg: 'text-sky-600', path: <><path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2V5Z" /><path d="M8 7h6" /><path d="M8 11h6" /></> },
  products: { bg: 'bg-amber-100', fg: 'text-amber-600', path: <><path d="M3 7l9-4 9 4-9 4-9-4Z" /><path d="M3 7v10l9 4 9-4V7" /><path d="M12 11v10" /></> },
  avatars: { bg: 'bg-pink-100', fg: 'text-pink-600', path: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></> },
  playbook: { bg: 'bg-slate-200', fg: 'text-slate-600', path: <><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4Z" /><path d="M9 8h6" /><path d="M9 12h4" /></> },
  delegate: { bg: 'bg-teal-100', fg: 'text-teal-600', path: <><path d="M16 3h5v5" /><path d="M21 3l-7 7" /><path d="M11 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" /></> },
  plan: { bg: 'bg-violet-100', fg: 'text-violet-600', path: <><path d="M9 4h6" /><path d="M5 6h14v15H5z" /><path d="M9 11l2 2 4-4" /><path d="M9 17h6" /></> },
  budget: { bg: 'bg-emerald-100', fg: 'text-emerald-600', path: <><circle cx="12" cy="12" r="9" /><path d="M15 9.5c-.5-1-1.6-1.5-3-1.5-1.7 0-3 .8-3 2s1.3 1.7 3 2 3 .8 3 2-1.3 2-3 2c-1.4 0-2.5-.5-3-1.5" /><path d="M12 6v12" /></> },
  image: { bg: 'bg-fuchsia-100', fg: 'text-fuchsia-600', path: <><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></> },
  video: { bg: 'bg-rose-100', fg: 'text-rose-600', path: <><rect x="3" y="5" width="13" height="14" rx="2" /><path d="m16 10 5-3v10l-5-3" /></> },
  avatar_video: { bg: 'bg-rose-100', fg: 'text-rose-600', path: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="12" cy="10" r="3" /><path d="M7 20a5 5 0 0 1 10 0" /></> },
  voice: { bg: 'bg-orange-100', fg: 'text-orange-600', path: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></> },
  broll: { bg: 'bg-cyan-100', fg: 'text-cyan-600', path: <><path d="M4 8h16v12H4z" /><path d="M4 8l3-4h13l-3 4" /><path d="M10 12l4 2-4 2v-4Z" /></> },
  research_profile: { bg: 'bg-blue-100', fg: 'text-blue-600', path: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /><circle cx="11" cy="9.5" r="2" /><path d="M7.5 14.5a4 4 0 0 1 7 0" /></> },
  research_post: { bg: 'bg-blue-100', fg: 'text-blue-600', path: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /><rect x="8" y="8" width="6" height="6" rx="1" /></> },
  decupagem: { bg: 'bg-purple-100', fg: 'text-purple-600', path: <><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h7" /><circle cx="18" cy="16" r="3" /><path d="m20 18 2 2" /></> },
  render: { bg: 'bg-indigo-100', fg: 'text-indigo-600', path: <><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8" /><path d="M12 16v4" /><path d="m10 8 4 2-4 2V8Z" /></> },
  caption: { bg: 'bg-lime-100', fg: 'text-lime-700', path: <><path d="M4 5h16v11H8l-4 4V5Z" /><path d="M8 9h8" /><path d="M8 12h5" /></> },
  schedule: { bg: 'bg-green-100', fg: 'text-green-600', path: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M8 3v4" /><path d="M16 3v4" /><path d="m9 15 2 2 4-4" /></> },
  file: { bg: 'bg-slate-100', fg: 'text-slate-600', path: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5" /><path d="m10 13-2 2 2 2" /><path d="m14 13 2 2-2 2" /></> },
  web: { bg: 'bg-sky-100', fg: 'text-sky-700', path: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18" /><path d="M12 3a14 14 0 0 0 0 18" /></> },
  look: { bg: 'bg-violet-100', fg: 'text-violet-600', path: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></> },
  tool: { bg: 'bg-slate-100', fg: 'text-slate-500', path: <><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5Z" /></> },
};

export function ActivityIcon({ kind, running, size = 'md' }: { kind: ActivityKind | string; running?: boolean; size?: 'sm' | 'md' }) {
  const style = KIND_STYLE[(kind as ActivityKind)] || KIND_STYLE.tool;
  const box = size === 'sm' ? 'h-7 w-7' : 'h-9 w-9';
  const icon = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4.5 w-4.5';
  return (
    <span className={`relative flex ${box} shrink-0 items-center justify-center rounded-xl ${style.bg} ${style.fg}`}>
      {running && (
        <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white shadow">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
        </span>
      )}
      <svg className={icon} style={{ width: size === 'sm' ? 14 : 18, height: size === 'sm' ? 14 : 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {style.path}
      </svg>
    </span>
  );
}

export function formatCredits(n: number | string | null | undefined, digits = 2): string {
  const v = Number(n || 0);
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: v > 0 && v < 0.01 ? 4 : digits });
}

export function formatDuration(startIso?: string, endIso?: string | null): string {
  if (!startIso) return '';
  const ms = (endIso ? new Date(endIso).getTime() : Date.now()) - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}min ${s % 60}s`;
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

// Markdown mínimo e seguro: escapa tudo e só então aplica negrito, itálico, código, links
// http(s), títulos curtos e listas.
function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function inline(s: string) {
  return s
    .replace(/`([^`]+)`/g, '<code class="rounded bg-slate-100 px-1 py-0.5 text-[0.85em]">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-indigo-600 underline underline-offset-2">$1</a>');
}

export function markdownToHtml(md: string): string {
  const lines = escapeHtml(String(md || '')).split(/\r?\n/);
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const ul = /^\s*[-•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const h = /^#{1,4}\s+(.*)$/.exec(line);
    if (ul) {
      if (list !== 'ul') { closeList(); out.push('<ul class="list-disc pl-5 space-y-0.5">'); list = 'ul'; }
      out.push(`<li>${inline(ul[1])}</li>`);
    } else if (ol) {
      if (list !== 'ol') { closeList(); out.push('<ol class="list-decimal pl-5 space-y-0.5">'); list = 'ol'; }
      out.push(`<li>${inline(ol[1])}</li>`);
    } else if (h) {
      closeList();
      out.push(`<p class="font-bold text-slate-900">${inline(h[1])}</p>`);
    } else if (!line.trim()) {
      closeList();
      out.push('<div class="h-2"></div>');
    } else {
      closeList();
      out.push(`<p>${inline(line)}</p>`);
    }
  }
  closeList();
  return out.join('');
}

// Memo: o markdown só é re-processado quando o texto muda (antes, a cada polling).
export const MarkdownText = memo(function MarkdownText({ text, className = '' }: { text: string; className?: string }) {
  const html = useMemo(() => markdownToHtml(text), [text]);
  return <div className={`space-y-1 break-words text-sm leading-relaxed ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
});

export const STATUS_META: Record<string, { label: string; cls: string; dot: string }> = {
  queued: { label: 'Na fila', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200', dot: 'bg-indigo-300 animate-pulse' },
  running: { label: 'Trabalhando', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200', dot: 'bg-indigo-500 animate-pulse' },
  waiting_approval: { label: 'Aguardando sua aprovação', cls: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500 animate-pulse' },
  idle: { label: 'Pronto', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  interrupted: { label: 'Interrompido', cls: 'bg-orange-50 text-orange-700 ring-orange-200', dot: 'bg-orange-500' },
  error: { label: 'Parou com erro', cls: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500' },
};
